import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { Info } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  INFO_ICON_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"
import {
  isNamedPreset,
  normalizeLegacyPresetValue,
  presetSelectionValue,
  readDetectionPreset,
  writeNamedPreset,
} from "../utils/presetVocabulary"

interface AbruptPitchChangesConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const PRESET_OPTIONS = [
  {
    key: "strict",
    label: "Strict",
    description: "Detects smaller pitch jumps",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Catches noticeable pitch jumps",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags very obvious pitch jumps",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const AbruptPitchChangesConfig: React.FC<
  AbruptPitchChangesConfigProps
> = ({ metricRecordData, onChange }) => {
  const metadata = metricRecordData.metric_metadata
  const canonicalPreset = Reflect.get(metadata, "detection_preset")
  const rawPreset =
    canonicalPreset !== undefined ? canonicalPreset : metadata.threshold_preset
  const recognizedPreset = normalizeLegacyPresetValue(rawPreset)
  const presetSelection = readDetectionPreset(
    metadata,
    ["significant_changes_threshold_hz"],
    ["min_change_for_variation_hz"],
  )
  const isCustom = presetSelection.kind === "custom"

  const handlePresetChange = (value: string) => {
    if (value === "custom") {
      const base = isNamedPreset(recognizedPreset) ? recognizedPreset : "normal"
      onChange({
        ...metricRecordData,
        metric_metadata: {
          ...metadata,
          detection_preset: base,
          threshold_preset: "custom",
        },
      })
      return
    }
    if (!isNamedPreset(value)) {
      return
    }
    onChange({
      ...metricRecordData,
      metric_metadata: writeNamedPreset(
        metadata,
        "detection_preset",
        "threshold_preset",
        value,
        ["significant_changes_threshold_hz"],
      ),
    })
  }

  const handleSignificantChangesThresholdHzChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        significant_changes_threshold_hz: isNaN(value) ? undefined : value,
      },
    })
  }

  const handleMinChangeForVariationHzChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseFloat(e.target.value)
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        min_change_for_variation_hz: isNaN(value) ? undefined : value,
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <span className={LABEL_STYLES}>Threshold Preset</span>
        </div>
        <Select
          value={presetSelectionValue(presetSelection)}
          onValueChange={handlePresetChange}
        >
          <SelectTrigger aria-label="Threshold Preset">
            <SelectValue placeholder="Select preset" />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map((option) => (
              <SelectItem
                key={option.key}
                value={option.key}
                description={option.description}
              >
                {option.label}
              </SelectItem>
            ))}
            {presetSelection.kind === "unknown" && (
              <SelectItem value="unknown" disabled>
                {presetSelection.label}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-coval-text-muted">
          Select a preset or choose Custom to define your own thresholds.
        </p>
      </div>

      {isCustom && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>
                Significant Changes Threshold (Hz)
              </span>
              <Tooltip content="Minimum pitch change in Hz to be considered significant.">
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={
                metricRecordData.metric_metadata
                  ?.significant_changes_threshold_hz ?? ""
              }
              onChange={handleSignificantChangesThresholdHzChange}
              placeholder="2"
              aria-label="Significant Changes Threshold (Hz)"
            />
          </div>

          <div className={FIELD_CONTAINER_STYLES}>
            <div className={`${LABEL_ROW_STYLES} min-h-10`}>
              <span className={LABEL_STYLES}>
                Min Change for Variation (Hz)
              </span>
              <Tooltip content="Minimum pitch change in Hz to count as variation.">
                <button type="button" className="inline-flex" aria-label="Info">
                  <Info className={INFO_ICON_STYLES} />
                </button>
              </Tooltip>
            </div>
            <Input
              type="number"
              step="0.1"
              min="0"
              value={
                metricRecordData.metric_metadata?.min_change_for_variation_hz ??
                ""
              }
              onChange={handleMinChangeForVariationHzChange}
              placeholder="1"
              aria-label="Min Change for Variation (Hz)"
            />
          </div>
        </div>
      )}
    </div>
  )
}
